import { ROLES, ROLE_IDS_BRAND, ROLE_IDS_OP } from '@auth';
import { defineStore } from 'pinia';
import { filter, tap } from 'rxjs';

import { auth } from '~/support/constants';
import type { UserInfo } from '~/types';

export const useUserStore = defineStore('user', () => {
  const accessToken = useCookie(auth.cookies.ACCESS_TOKEN);
  const expiresAt = useCookie(auth.cookies.EXPIRES_AT);
  const { identity } = useGateway();

  const authenticated = ref(false);
  const user = ref<UserInfo | null>(null);
  const loading = ref(true);

  const account = computed(() => {
    return user.value?.accountId;
  });

  const sessionExpired = computed(() => {
    if (!expiresAt.value) return true;

    return Date.now() > Number(expiresAt.value);
  });

  function fetchUserInfo() {
    return identity.users.me().pipe(
      filter(res => !!res),
      tap(res => {
        user.value = { ...user.value, ...res.data };
        loading.value = false;
      }),
    );
  }

  function sendResetPasswordRequest(email: string) {
    return identity.auth.password.requestChange(email).subscribe();
  }

  const initials = computed(() => {
    if (!user.value?.given_name || !user.value?.family_name) return '';

    return `${user.value.given_name[0]}${user.value.family_name[0]}`;
  });

  const username = computed(() => {
    return user.value?.name;
  });

  const roleName = computed(() => {
    if (!user.value) return null;

    return user.value.role?.name;
  });

  const isAdmin = computed(() => {
    return roleName.value === ROLES.opAdmin || roleName.value === ROLES.opFinance;
  });

  const isOpUser = computed(() => {
    if (!user.value?.role) return false;

    return ROLE_IDS_OP.includes(user.value.role.id) && user.value.role.name.startsWith('orderprotection');
  });

  const isBrandUser = computed(() => {
    if (!user.value?.role) return false;

    return ROLE_IDS_BRAND.includes(user.value.role.id);
  });

  const isGlobalSupport = computed(() => {
    return roleName.value === ROLES.opGlobalSupport;
  });

  return {
    accessToken,
    account,
    authenticated,
    fetchUserInfo,
    initials,
    isAdmin,
    isBrandUser,
    isGlobalSupport,
    isOpUser,
    loading,
    roleName,
    sendResetPasswordRequest,
    sessionExpired,
    user,
    username,
  };
});
